import React, { useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Input, Modal } from 'antd';
import { ArrowBack } from '@material-ui/icons';
import RouletteWheel, { RouletteSegment, RouletteWheelRef } from './RouletteWheel';
import Loading from 'components/Basic/Loading';
import toast from 'components/Basic/Toast';
import {
  useLazyGetPrizeDrawDetailQuery,
  useSpinPrizeDrawMutation,
  useReviewEntryMutation,
} from 'core/worldCup/prizeDraws/PrizeDrawsService';
import { PrizeDrawDetail, SpinResultEntry, DetailItem, DetailEntry, EntryStatus } from 'core/worldCup/prizeDraws/types';
import { Img } from 'react-image';
import { getImageLink } from 'utilities/common';
import { getApiErrorMessage } from 'utilities/getApiErrorMessages';

type PrizeDrawsRouletteProps = {
  prizeDrawId: number;
  onBack: () => void;
};

type ReviewAction = {
  entryId: number;
  status: 'WINNER' | 'INVALID';
  participantName: string;
};

const DECORATIVE_SEGMENT_COUNT = 16;

const buildDecorativeSegments = (): RouletteSegment[] =>
  Array.from({ length: DECORATIVE_SEGMENT_COUNT }, (_, i) => ({
    id: i + 1,
    label: String(i + 1),
  }));

const STATUS_ORDER: EntryStatus[] = ['SELECTED', 'WINNER', 'BACKUP', 'INVALID'];

const PrizeDrawsRoulette: React.FC<PrizeDrawsRouletteProps> = ({ prizeDrawId, onBack }) => {
  const intl = useIntl();
  const [detail, setDetail] = useState<PrizeDrawDetail | null>(null);
  const [segments] = useState<RouletteSegment[]>(buildDecorativeSegments());
  const [isLoading, setIsLoading] = useState(true);
  const [isSpinning, setIsSpinning] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [canSpin, setCanSpin] = useState(true);
  const [lastWinner, setLastWinner] = useState<SpinResultEntry | null>(null);
  const [showReveal, setShowReveal] = useState(false);
  const [revealRound, setRevealRound] = useState(0);
  const [revealPrizeName, setRevealPrizeName] = useState('');
  const [reviewAction, setReviewAction] = useState<ReviewAction | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);

  const wheelRef = useRef<RouletteWheelRef>(null);
  const pendingSpinResult = useRef<SpinResultEntry | null>(null);
  const [getPrizeDrawDetail] = useLazyGetPrizeDrawDetailQuery();
  const [spinPrizeDraw] = useSpinPrizeDrawMutation();
  const [reviewEntry] = useReviewEntryMutation();

  useEffect(() => {
    loadDetail();
  }, [prizeDrawId]);

  const getCompletedRounds = (items: DetailItem[]): number => {
    const confirmedRounds = new Set<number>();
    items?.forEach(item => {
      item.rounds?.forEach(round => {
        const hasWinner = round.attempts?.some(attempt =>
          attempt.entries?.some(entry => entry.status === 'WINNER')
        );
        if (hasWinner) confirmedRounds.add(round.roundNumber);
      });
    });
    return confirmedRounds.size;
  };

  const loadDetail = async () => {
    setIsLoading(true);
    try {
      const res = await getPrizeDrawDetail(prizeDrawId).unwrap();
      setDetail(res);
      const total = res.mainWinners || 0;
      setTotalRounds(total);
      const completed = res.roundNumber ?? getCompletedRounds(res.items || []);
      const isFinished = res.status === 'COMPLETED' || res.status === 'CANCELLED';
      setCurrentRound(isFinished ? completed : Math.min(completed + 1, total));
      if (isFinished) setCanSpin(false);
    } catch (e) {
      console.error('Error loading prize draw detail:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshDetail = async () => {
    try {
      const res = await getPrizeDrawDetail(prizeDrawId).unwrap();
      setDetail(res);
      const completed = res.roundNumber ?? getCompletedRounds(res.items || []);
      const isFinished = res.status === 'COMPLETED' || res.status === 'CANCELLED';
      const total = res.mainWinners || totalRounds;
      setCurrentRound(isFinished ? completed : Math.min(completed + 1, total));
      if (isFinished) {
        setCanSpin(false);
      } else {
        setCanSpin(true);
      }
    } catch (_) {}
  };

  const handleSpin = async () => {
    if (isSpinning || !canSpin) return;

    setIsSpinning(true);
    setShowReveal(false);
    setLastWinner(null);
    wheelRef.current?.startSpin();

    try {
      const result = await spinPrizeDraw(prizeDrawId).unwrap();
      setCurrentRound(result.roundNumber);
      setCanSpin(result.canSpin);

      if (result.reason === 'PENDING_REVIEW') {
        toast.info({ title: intl.formatMessage({ id: 'Pending review message' }) });
        wheelRef.current?.reset();
        setIsSpinning(false);
        return;
      }

      if (!result.didSpin) {
        toast.error({ title: intl.formatMessage({ id: 'Spin could not be executed' }) });
        wheelRef.current?.reset();
        setIsSpinning(false);
        return;
      }

      if (result.reason === 'SPIN_EXECUTED' && result.results.length > 0) {
        const winner = result.results.find(r => r.status === 'SELECTED') || result.results[0];
        pendingSpinResult.current = winner;
        setRevealRound(result.roundNumber);
        setRevealPrizeName(result.item?.reward?.name || '');
      }

      wheelRef.current?.stopSpin();
    } catch (e: any) {
      console.error('Error executing spin:', e);
      const defaultMsg = intl.formatMessage({ id: 'Error executing spin' });
      toast.error({ title:  getApiErrorMessage(e, intl.locale, defaultMsg) });
      wheelRef.current?.reset();
      setIsSpinning(false);
    }
  };

  const handleSpinEnd = async () => {
    setIsSpinning(false);
    if (pendingSpinResult.current) {
      setLastWinner(pendingSpinResult.current);
      pendingSpinResult.current = null;
      await refreshDetail();
      setShowReveal(true);
    }
  };

  const handleCloseReveal = () => {
    setShowReveal(false);
  };

  const handleOpenReview = (entryId: number, status: 'WINNER' | 'INVALID', participantName: string) => {
    setReviewAction({ entryId, status, participantName });
    setReviewNotes('');
  };

  const handleCloseReview = () => {
    setReviewAction(null);
    setReviewNotes('');
  };

  const handleConfirmReview = async () => {
    if (!reviewAction || !reviewNotes.trim()) return;
    setIsReviewing(true);
    try {
      await reviewEntry({
        entryId: reviewAction.entryId,
        status: reviewAction.status,
        notes: reviewNotes.trim(),
      }).unwrap();
      toast.success({
        title: intl.formatMessage({
          id: reviewAction.status === 'WINNER' ? 'Marked as winner' : 'Entry invalidated',
        }),
      });
      handleCloseReview();
      await refreshDetail();
    } catch (e: any) {
      console.error('Error reviewing entry:', e);
      const defaultMsg = intl.formatMessage({ id: 'Error reviewing entry' });
      toast.error({ title:  getApiErrorMessage(e, intl.locale, defaultMsg) });
    } finally {
      setIsReviewing(false);
    }
  };

  const isFinished = !canSpin && !isSpinning;
  const hasItems = detail?.items && detail.items.length > 0;
  const hasAnyEntry = detail?.items?.some(item =>
    item.rounds?.some(round => round.attempts?.some(attempt => attempt.entries?.length > 0))
  );

  const renderEntryActions = (entry: DetailEntry) => {
    if (entry.status === 'WINNER' || entry.status === 'INVALID') return null;

    return (
      <div className="entry_actions">
        {(entry.status === 'SELECTED' || entry.status === 'BACKUP') && (
          <Button
            className="review_btn mark_winner_btn"
            size="small"
            onClick={() => handleOpenReview(entry.entryId, 'WINNER', entry.participant?.fullName)}
          >
            <FormattedMessage id="Mark as winner" />
          </Button>
        )}
        {(entry.status === 'SELECTED' || entry.status === 'BACKUP') && (
          <Button
            className="review_btn invalidate_btn"
            size="small"
            onClick={() => handleOpenReview(entry.entryId, 'INVALID', entry.participant?.fullName)}
          >
            <FormattedMessage id="Invalidate" />
          </Button>
        )}
      </div>
    );
  };

  const renderResultsByRound = (item: DetailItem, roundOffset: number) => {
    if (!item.rounds || item.rounds.length === 0) return null;

    return item.rounds.map(round => (
      round.attempts?.map(attempt => {
        const entriesByStatus: Record<string, DetailEntry[]> = {};
        attempt.entries?.forEach(entry => {
          if (!entriesByStatus[entry.status]) entriesByStatus[entry.status] = [];
          entriesByStatus[entry.status].push(entry);
        });

        const hasEntries = attempt.entries && attempt.entries.length > 0;
        if (!hasEntries) return null;

        const globalRound = roundOffset + round.roundNumber;

        return (
          <div className="round_attempt_block" key={`${globalRound}-${attempt.attemptNumber}`}>
            <div className="round_attempt_header">
              <span className="round_label">
                <FormattedMessage id="Round" /> {globalRound}
              </span>
              <span className="attempt_label">
                <FormattedMessage id="Attempt" /> {attempt.attemptNumber}
              </span>
            </div>
            {STATUS_ORDER.map(status => {
              const entries = entriesByStatus[status];
              if (!entries || entries.length === 0) return null;

              return (
                <div className={`results_section status_${status.toLowerCase()}`} key={status}>
                  <h4>
                    {intl.formatMessage({ id: `entry_status_${status.toLowerCase()}` })}
                    {' '}({entries.length})
                  </h4>
                  {entries.map((entry, idx) => (
                    <div className={`result_item status_${entry.status.toLowerCase()}`} key={entry.entryId}>
                      <span className="position">{idx + 1}.</span>
                      <div className="entry_info">
                        <span className="name">{entry.participant?.fullName}</span>
                        {entry.participant?.phone && (
                          <span className="entry_detail">{entry.participant.phone}</span>
                        )}
                        {entry.participant?.participationCode && (
                          <span className="entry_detail">
                            <FormattedMessage id="Code" />: {entry.participant.participationCode}
                          </span>
                        )}
                        {item.reward?.hasAmericanVisa && (
                          <span className={`visa_status ${entry.participant?.hasAmericanVisa ? 'has_visa' : 'no_visa'}`}>
                            {intl.formatMessage({ id: entry.participant?.hasAmericanVisa ? 'Has VISA' : 'No VISA' })}
                          </span>
                        )}
                        {entry.notes && (
                          <span className="entry_detail notes">{entry.notes}</span>
                        )}
                      </div>
                      {renderEntryActions(entry)}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        );
      })
    ));
  };

  return (
    <div className="prizeDraws_roulette_wrapper">
      <div className="roulette_header">
        <Button className="back_btn" onClick={onBack}>
          <ArrowBack />
          <FormattedMessage id="Back" />
        </Button>
        <h2><FormattedMessage id="Prize draw roulette" /></h2>
        {detail && (
          <span className="round_indicator">
            <FormattedMessage id="Round" /> {currentRound} / {totalRounds}
          </span>
        )}
      </div>

      {isLoading && <Loading visible={isLoading} />}

      {!isLoading && (
        <div className="roulette_body">
          <div className="roulette_results">
            <h3><FormattedMessage id="Results" /></h3>
            {hasItems && hasAnyEntry ? (
              (() => {
                let roundOffset = 0;
                return detail!.items.map((item: DetailItem) => {
                  const itemHasEntries = item.rounds?.some(round =>
                    round.attempts?.some(attempt => attempt.entries?.length > 0)
                  );
                  if (!itemHasEntries) return null;

                  const currentOffset = roundOffset;
                  const maxRound = item.rounds?.reduce((max, r) => Math.max(max, r.roundNumber), 0) || 0;
                  roundOffset += maxRound;

                  return (
                    <div className="prize_item_block" key={item.prizePoolItemId}>
                      {item.reward?.image && (
                        <div className="prize_image">
                          <Img src={getImageLink(item.reward.image)} alt={item.reward.name} />
                        </div>
                      )}
                      <div className="prize_item_title">
                        <span>{item.reward?.name}</span>
                        {item.reward?.hasAmericanVisa && (
                          <span className="visa_badge"><FormattedMessage id="Requires VISA" /></span>
                        )}
                      </div>
                      {renderResultsByRound(item, currentOffset)}
                    </div>
                  );
                });
              })()
            ) : (
              <p className="no_results_text"><FormattedMessage id="No results yet" /></p>
            )}

            {isFinished && (
              <Button className="back_to_list_btn" onClick={onBack}>
                <FormattedMessage id="Back to list" />
              </Button>
            )}
          </div>

          <div className="roulette_canvas_area">
            {detail?.image && (
              <div className="event_image_banner">
                <Img src={getImageLink(detail.image)} alt="Event" />
              </div>
            )}
            <RouletteWheel
              ref={wheelRef}
              segments={segments}
              diameter={400}
              onSpinEnd={handleSpinEnd}
            />
            <div className="spin_controls">
              {!isSpinning && canSpin && !showReveal && (
                <Button
                  className="spin_btn"
                  onClick={handleSpin}
                >
                  <FormattedMessage id={currentRound === 0 ? 'Spin' : 'Spin again'} />
                </Button>
              )}
              {isSpinning && (
                <p className="spinning_text"><FormattedMessage id="Spinning..." /></p>
              )}
            </div>

          </div>

          {showReveal && lastWinner && (
            <div className="reveal_overlay" onClick={handleCloseReveal}>
              <div className="reveal_card" onClick={e => e.stopPropagation()}>
                <div className="reveal_icon">🏆</div>
                {revealPrizeName && (
                  <p className="reveal_prize">{revealPrizeName}</p>
                )}
                <h3 className="reveal_round">
                  <FormattedMessage id="Round" /> {revealRound}
                </h3>
                <h2 className="reveal_name">{lastWinner.fullName}</h2>
                {lastWinner.participationCode && (
                  <p className="reveal_code">
                    <FormattedMessage id="Code" />: {lastWinner.participationCode}
                  </p>
                )}
                <Button className="reveal_close_btn" onClick={handleCloseReveal}>
                  <FormattedMessage id={canSpin ? 'Continue' : 'Close'} />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Modal
        className="review_entry_modal"
        visible={!!reviewAction}
        onCancel={handleCloseReview}
        title={
          reviewAction?.status === 'WINNER'
            ? intl.formatMessage({ id: 'Mark as winner' })
            : intl.formatMessage({ id: 'Invalidate entry' })
        }
        footer={null}
      >
        <div className="review_modal_content">
          <p className="review_participant_name">{reviewAction?.participantName}</p>
          <p className="review_description">
            <FormattedMessage
              id={reviewAction?.status === 'WINNER' ? 'Mark winner reason' : 'Invalidate reason'}
            />
          </p>
          <Input.TextArea
            rows={3}
            value={reviewNotes}
            onChange={e => setReviewNotes(e.target.value)}
            placeholder={intl.formatMessage({ id: 'Enter reason' })}
          />
          <div className="side_btns">
            <div className="btn_wrapper">
              <Button className="cancel_btn" onClick={handleCloseReview}>
                <FormattedMessage id="Cancel" />
              </Button>
              <Button
                className="save_btn"
                loading={isReviewing}
                disabled={!reviewNotes.trim()}
                onClick={handleConfirmReview}
              >
                <FormattedMessage id="Confirm" />
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default PrizeDrawsRoulette;
